// ============================================================
//  tourContent.js
//  CODiii — App Tour & Tooltip Copy
//
//  OWNER: Paige (OQULi Inc.)
//  This file contains all user-facing text for the app tour
//  and standalone tooltips. Edit copy here freely.
//
//  IMPORTANT: Do not add logic to this file — copy only.
//  To attach a tooltip to a new DOM element, ask a developer
//  to update AppTour.jsx.
// ============================================================


// ============================================================
//  TOUR STEPS
//  5 steps (Starter) or 6 steps (Studio/Firm — adds Compliance Scan).
//  Exit button (×) appears on every step.
//  Progress bar shown throughout.
//  Resume state is saved — users can exit and pick up from
//  the checklist where they left off.
// ============================================================

export const tourSteps = {

  // ----------------------------------------------------------
  //  STEP 1 — Welcome
  //  Modal (no UI target). Launches Create Project wizard.
  //  Two body variants below — AppTour.jsx picks `body` for Studio/Firm
  //  (6 steps, includes Compliance Scan) or `bodyStarter` for Starter
  //  (5 steps, no Compliance Scan) based on the user's plan.
  // ----------------------------------------------------------
  welcome: {
    title: 'Welcome to CODiii',
    body: `CODiii turns your project documents into a living compliance checklist, so nothing slips through.
        <ul class="tour-steps-list">
          <li><strong>Step 1</strong> — Link a project folder and classify documents</li>
          <li><strong>Step 2</strong> — Ask questions while requirement extraction runs</li>
          <li><strong>Step 3</strong> — Set up your Project Settings while extraction finishes</li>
          <li><strong>Step 4</strong> — Review your Requirements Register, every project requirement in one place</li>
          <li><strong>Step 5</strong> — Run a compliance scan</li>
        </ul>

        <div class="tour-tip-box">
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><circle cx="8" cy="8" r="7" stroke="#f57c00" stroke-width="1.5"/><path d="M8 7v4M8 5h.01" stroke="#f57c00" stroke-width="1.5" stroke-linecap="round"/></svg>
          <p>Takes about 3 minutes. Exit any time and resume from your checklist.</p>
        </div>`,
    bodyStarter: `CODiii turns your project documents into a living compliance checklist, so nothing slips through.
        <ul class="tour-steps-list">
          <li><strong>Step 1</strong> — Link a project folder and classify documents</li>
          <li><strong>Step 2</strong> — Ask questions while requirement extraction runs</li>
          <li><strong>Step 3</strong> — Set up your Project Settings while extraction finishes</li>
          <li><strong>Step 4</strong> — Review your Requirements Register, every project requirement in one place</li>
        </ul>

        <div class="tour-tip-box">
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><circle cx="8" cy="8" r="7" stroke="#f57c00" stroke-width="1.5"/><path d="M8 7v4M8 5h.01" stroke="#f57c00" stroke-width="1.5" stroke-linecap="round"/></svg>
          <p>Takes about 3 minutes. Exit any time and resume from your checklist.</p>
        </div>`,
    buttons: {
      exit: 'Exit Tour',
      next: 'Create Your First Project →',
    },
  },


  // ----------------------------------------------------------
  //  STEP 2 — Link Folder + Classify Docs
  //  Anchored callout on the folder URL input field.
  //  Shown after the Create Project wizard completes.
  // Take the user to the Research page upon completion of the wizard
  //
  //  NOTE FOR DEV: pulse the #folder-url-input field.
  //  Classify Docs note appears as a sub-callout after
  //  folder is linked (same step, second state).
  // ----------------------------------------------------------
  linkAndClassify: {

    link: {
      title: 'Link your project folder',
      body: `Paste a shared folder URL and click <strong>+ Add Folder</strong>.
            <ul class="tour-steps-list">
              <li><strong>Supported</strong>: Google Drive and SharePoint shared folder URLs</li>
              <li><strong>File types</strong>: PDFs and DOCX only</li>
            </ul>

            <div class="tour-highlight">
              Keep your folder flat. CODiii won't index files nested inside subfolders.
            </div>`,
            buttons: { exit: 'Exit Tour', back: 'Back', next: 'Next' },
          },

          classify: {
            title: 'Confirm document roles',
            body: `CODiii <strong>detects</strong> a suggested type for each file (Requirement or Deliverable). Review and correct any that look wrong, then click <strong>Begin Indexing</strong> to make your choices official.
                <ul class="tour-steps-list">
                  <li><strong>Requirement</strong>: anything your project is governed by — building codes, bylaws, client programs, standards</li>
                  <li><strong>Deliverable</strong>: anything you produce to prove your design meets those requirements — drawings, specifications, schedules</li>
                </ul>

                <div class="tour-tip-box">
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><circle cx="8" cy="8" r="7" stroke="#f57c00" stroke-width="1.5"/><path d="M8 7v4M8 5h.01" stroke="#f57c00" stroke-width="1.5" stroke-linecap="round"/></svg>
                  <p>Extraction runs in the background and takes <strong>5–10 minutes</strong>. You don't need to wait on this screen.</p>
                </div>`,
      buttons: { exit: 'Exit Tour', back: 'Back', next: 'Begin Extraction' },
    },

  },


  // ----------------------------------------------------------
  //  STEP 3 — Research
  //  Anchored callout on the document selection panel.
  //  Available immediately — no need to wait for extraction.
  // ----------------------------------------------------------
  research: {
    title: 'Ask questions about your project',
    body: `Skip the manual document search. Ask CODiii anything about your project in plain language. It searches across your selected files and cites every answer back to the source.
      <ul class="tour-steps-list">
        <li><strong>Select documents</strong> from the panel on the right</li>
        <li><strong>Ask anything</strong>: "What are the occupancy requirements for this building?"</li>
        <li><strong>Every answer</strong> is cited back to the source document and page</li>
      </ul>

      <div class="tour-highlight">
        CODiii only searches your indexed project documents. It does not have internet access.
      </div>`,
    buttons: { exit: 'Exit Tour', back: 'Back', next: 'Next' },
  },


  // ----------------------------------------------------------
  //  STEP 5 — Requirements Register
  //  Anchored callout on the Register's search bar.
  //  Auto-advances once the user starts typing — no Next button.
  // ----------------------------------------------------------
  registerViewed: {
    title: 'Your Requirements Register',
    body: `Every requirement CODiii extracts lands here, organized and searchable.
      <ul class="tour-steps-list">
        <li><strong>Search</strong>: find a specific requirement by keyword</li>
        <li><strong>Filter by compliance type</strong>: Building Code, Client Requirements, Zoning, and more</li>
        <li><strong>Click the blue button</strong> to see exactly where it came from in the source document</li>
      </ul>

      <div class="tour-highlight">
        Try typing in the search bar for something now. This step finishes itself once you start typing.
      </div>`,
    buttons: { exit: 'Exit Tour', back: 'Back' },
  },


  // ----------------------------------------------------------
  //  STEP 6 — Run Your First Scan
  //  Since there is a time delay between the completion of the wizard and the extraction, this step of the tour would be triggered when the notification bell goes off for the extraction completion.
  //  Anchored callout on the New Scan button.
  //  Studio+ only — hidden for Starter tier.
  //
  //  NOTE FOR DEV: skip this step entirely for Starter users —
  //  Starter never reaches it (excluded from buildMilestones), so the
  //  tour ends at step 5 (Requirements Register) for that tier.
  // ----------------------------------------------------------
  complianceScan: {
    title: 'Run your first scan',
    body: `Your requirements have been extracted and are ready to scan against.
        <ul class="tour-steps-list">
          <li>Click <strong>New Scan</strong> to get started</li>
          <li>Select a <strong>compliance type</strong> and the <strong>deliverables</strong> you want checked</li>
          <li>CODiii flags non-compliant items and logs what it verified</li>
        </ul>

        <div class="tour-tip-box">
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><circle cx="8" cy="8" r="7" stroke="#f57c00" stroke-width="1.5"/><path d="M8 7v4M8 5h.01" stroke="#f57c00" stroke-width="1.5" stroke-linecap="round"/></svg>
          <p>Start with <strong>Client Requirements</strong> or <strong>Building Code</strong> — these tend to produce the clearest results on a first scan.</p>
        </div>`,
    buttons: { exit: 'Exit Tour', back: 'Back', next: 'Next' },
  },

  // ----------------------------------------------------------
  //  STEP 4 — Project Settings + Invite Team
  //  Nav-pulse reminder until the user navigates to Project Settings,
  //  then this card centers on screen with the background dimmed.
  //  Shown right after Research, before Register/Scan — gives users
  //  something useful to do while background extraction finishes.
  //  Two variants below — AppTour.jsx picks `title`/`body` for Studio/Firm
  //  (team invites are available) or `titleStarter`/`bodyStarter` for
  //  Starter (no team seats, so no mention of inviting collaborators).
  // ----------------------------------------------------------
  projectSettings: {
    title: 'Project Settings + add your team',
    body: `Keep your project details accurate. Better settings mean better requirement extraction and scan results.
        <ul class="tour-steps-list">
          <li><strong>Occupancy + construction type</strong>: CODiii uses these to contextualize building code requirements</li>
          <li><strong>Compliance context</strong>: jurisdiction, applicable codes, and project parameters</li>
          <li><strong>Team members</strong>: invite collaborators to this project from the Settings tab</li>
        </ul>

        <div class="tour-highlight">
          Fill out Settings before running your first scan — incomplete project details reduce scan accuracy.
        </div>`,
    titleStarter: 'Project Settings',
    bodyStarter: `Keep your project details accurate. Better settings mean better requirement extraction and scan results.
        <ul class="tour-steps-list">
          <li><strong>Occupancy + construction type</strong>: CODiii uses these to contextualize building code requirements</li>
          <li><strong>Compliance context</strong>: jurisdiction, applicable codes, and project parameters</li>
        </ul>

        <div class="tour-highlight">
          Fill out Settings before running your first scan — incomplete project details reduce scan accuracy.
        </div>`,
    buttons: { exit: 'Exit Tour', back: 'Back', next: 'Next' },
  },

};


// ============================================================
//  TOOLTIPS
//  Standalone tooltips triggered by ? icons.
//  Independent of tour state.
// ============================================================

export const tooltips = {

  // Notifications bell — bottom-left, all pages
  notificationsBell: {
    title: 'Notifications',
    body: `Updates on your project — extraction progress, scan results, resolved issues, and more. Different from the chat icon (bottom-right), which connects you to the CODiii support team.`,
  },

  // Requirements Register — compliance type filter
  complianceTypeFilter: {
    title: 'Compliance Types',
    body: `Use the filter to focus the Register by compliance type.
          <ul class="tour-steps-list">
            <li><strong>Building Code</strong> — filter further by discipline (Life Safety, Structural, Mechanical, and more)</li>
            <li><strong>Client Requirements</strong> — owner briefs and RFP conditions</li>
            <li><strong>Zoning</strong> — municipal bylaws and land use rules</li>
            <li><strong>Standards</strong> — referenced technical standards (ASHRAE, ANSI, etc.)</li>
            <li><strong>Certifications</strong> — LEED, WELL, BOMA BEST</li>
          </ul>
          <div class="tour-tip-box">
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><circle cx="8" cy="8" r="7" stroke="#f57c00" stroke-width="1.5"/><path d="M8 7v4M8 5h.01" stroke="#f57c00" stroke-width="1.5" stroke-linecap="round"/></svg>
            <p><strong>Tip:</strong> Building Code is the only type with sub-discipline filtering — the others filter at the type level only.</p>
          </div>`, 
  },

  // Requirements Register — Source Documents panel
  sourceDocumentsPanel: {
    title: 'Source Documents',
    body: `Click any document in this panel to filter the Register to requirements from that file only.

          <div class="tour-tip-box">
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><circle cx="8" cy="8" r="7" stroke="#f57c00" stroke-width="1.5"/><path d="M8 7v4M8 5h.01" stroke="#f57c00" stroke-width="1.5" stroke-linecap="round"/></svg>
            <p><strong>Useful for:</strong> reviewing everything a specific code or client brief requires before running a scan.</p>
          </div>

          <div class="tour-highlight">
            Only documents indexed as Requirement role appear here — deliverables won't show up.
          </div>`,
  },

  // Research — document selection panel
  researchDocumentPanel: {
    title: 'Select Documents',
    body: `Choose which project documents CODiii searches before asking your question.
          <ul class="tour-steps-list">
            <li><strong>Select multiple</strong> — CODiii searches all of them at once</li>
            <li><strong>Source is cited</strong> — every answer tells you which file it came from</li>
            <li><strong>All roles searchable</strong> — requirements, deliverables, and records all appear here</li>
          </ul>

          <div class="tour-highlight">
            CODiii only searches your indexed project documents — it does not have internet access.
          </div>`,
  },

  // Compliance Tracker — scan types
  scanPaths: {
    title: 'Scan Types',
  body: `Choose the type of analysis CODiii runs against your documents.
        <ul class="tour-steps-list">
          <li><strong>Compliance Check</strong> — verifies your deliverables against a compliance type (Building Code, Client Requirements, etc.)</li>
          <li><strong>Conflict Detection</strong> — finds contradictions between two documents <span style="color:#e97024; font-weight:600;">· Coming soon</span></li>
          <li><strong>Gap Detection</strong> — identifies topics required by a standard that are missing from a deliverable <span style="color:#e97024; font-weight:600;">· Coming soon</span></li>
        </ul>

        <div class="tour-highlight">
          Only Compliance Check is available now — select a compliance type and one or more deliverables to run it.
        </div>`,
  },

  // Document classifier — Requirement vs. Deliverable
  documentRoles: {
    title: 'Document Roles',
    body: `Assign the correct role when indexing so CODiii knows how to use each file.
          <ul class="tour-steps-list">
            <li><strong>Requirement</strong> — the rules your design must meet (building codes, client briefs, bylaws, standards). CODiii extracts these into your Requirements Register.</li>
            <li><strong>Deliverable</strong> — what your team produces (drawings, specs, schedules). Checked against requirements at scan time. Never extracted.</li>
          </ul>

          <div class="tour-tip-box">
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><circle cx="8" cy="8" r="7" stroke="#f57c00" stroke-width="1.5"/><path d="M8 7v4M8 5h.01" stroke="#f57c00" stroke-width="1.5" stroke-linecap="round"/></svg>
            <p><strong>Not sure?</strong> Default to Deliverable — CODiii will never extract requirements from a file unless it's marked as a Requirement.</p>
          </div>`,
  },

};
